import React from 'react';
import { Box, Typography, Button, Card } from '@mui/material';
import { environment } from '../../../environments/environment';
import { FaTruck } from 'react-icons/fa';
import { GiPaperBagOpen } from 'react-icons/gi';
import {
  getStoreStatus,
  getTodaysClosingTime,
  ensureAmPmFormat,
} from '../../Utils/storeTimingUtils';

const BranchItem = ({
  index,
  selectedBranch,
  handleBranchSelect,
  store,
  onStartOrder,
}) => {
  // Get store status using the timing utilities
  const storeStatus = getStoreStatus(store);
  const closingTime = getTodaysClosingTime(store);

  // Ensure closingTime is always shown in 12-hour (AM/PM) format
  const displayClosingTime = ensureAmPmFormat(closingTime);

  return (
    <Card
      onClick={() => handleBranchSelect(index)}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: '20px',
        border:
          selectedBranch === index
            ? `2px solid ${environment.mainColor}`
            : '1px solid #f0f0f0',
        backgroundColor: selectedBranch === index ? '#fff8f5' : '#fff',
        boxShadow:
          selectedBranch === index
            ? `0 8px 32px -4px rgba(255, 107, 53, 0.25), 0 0 0 1px rgba(255, 107, 53, 0.05)`
            : '0 4px 16px -4px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)',
        '&:hover': {
          boxShadow:
            '0 12px 40px -8px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 107, 53, 0.1)',
          borderColor:
            selectedBranch === index ? environment.mainColor : '#e0e0e0',
        },
        p: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          background:
            selectedBranch === index
              ? `linear-gradient(135deg, ${environment.mainColor}08 0%, ${environment.mainColor}04 100%)`
              : 'transparent',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#1a1a1a',
              fontSize: '18px',
              fontFamily: 'Jost',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              textTransform: 'none',
            }}
          >
            {typeof (store.businessName || store.businessName) === 'string'
              ? store.businessName || store.businessName
              : 'Restaurant Name'}
          </Typography>

          {selectedBranch === index && (
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: environment.mainColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                }}
              />
            </Box>
          )}
        </Box>
        <Typography
          sx={{
            color: '#6b7280',
            fontSize: '14px',
            fontFamily: 'Jost',
            lineHeight: 1.4,
            fontWeight: 400,
            mb: 2,
          }}
        >
          {(() => {
            const addr = store.address || store.location;

            // If address is a simple string, show it
            if (typeof addr === 'string' && addr.trim().length > 0) {
              return addr;
            }

            // If address is an object with detailed fields, format them
            if (addr && typeof addr === 'object') {
              // Prefer displayName if provided
              if (addr.displayName && addr.displayName.trim()) {
                return addr.displayName;
              }

              const parts = [];
              if (addr.addressLine1 && addr.addressLine1.trim()) {
                parts.push(addr.addressLine1.trim());
              }
              // addressLine2 is optional
              if (addr.addressLine2 && addr.addressLine2.trim()) {
                parts.push(addr.addressLine2.trim());
              }

              // Prefer multiple fallbacks: if `city` is empty use `locality`,
              // if `state` is empty use `administrativeDistrictLevel1`,
              // and prefer `zipCode` but fallback to `postalCode`.
              const cityPart =
                (addr.city && addr.city.trim()) ||
                (addr.locality && addr.locality.trim());

              const statePart =
                (addr.state && addr.state.trim()) ||
                (addr.administrativeDistrictLevel1 &&
                  addr.administrativeDistrictLevel1.trim());

              const zipPart =
                (addr.zipCode && addr.zipCode.trim()) ||
                (addr.postalCode && addr.postalCode.trim());

              const cityStateZip = [cityPart, statePart, zipPart]
                .filter(Boolean)
                .join(', ');

              if (cityStateZip) parts.push(cityStateZip);

              // Fallback to route/streetNumber if addressLine1 missing
              if (parts.length === 0) {
                const routeParts = [];
                if (addr.streetNumber && addr.streetNumber.trim()) {
                  routeParts.push(addr.streetNumber.trim());
                }
                if (addr.route && addr.route.trim()) {
                  routeParts.push(addr.route.trim());
                }
                if (routeParts.length) parts.push(routeParts.join(' '));
              }

              if (parts.length > 0) return parts.join(' • ');
            }

            return 'Address not available';
          })()}
        </Typography>
        {/* Enhanced Status Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mb: 2,
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          {/* Current Status Badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1,
              borderRadius: '20px',
              // margin: { xs: '0 auto', sm: '0' },
              background: storeStatus.isOpen
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.08) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.08) 100%)',
              border: `1.5px solid ${
                storeStatus.isOpen ? '#10b981' : '#ef4444'
              }`,
              width: 'fit-content',
              boxShadow: storeStatus.isOpen
                ? '0 2px 8px rgba(16, 185, 129, 0.15)'
                : '0 2px 8px rgba(239, 68, 68, 0.15)',
              transition: 'all 0.3s ease',
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: storeStatus.isOpen
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                boxShadow: storeStatus.isOpen
                  ? '0 0 8px rgba(16, 185, 129, 0.4)'
                  : '0 0 8px rgba(239, 68, 68, 0.4)',
                animation: storeStatus.isOpen ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: `0 0 8px ${
                      storeStatus.isOpen
                        ? 'rgba(16, 185, 129, 0.4)'
                        : 'rgba(239, 68, 68, 0.4)'
                    }`,
                  },
                  '50%': {
                    boxShadow: `0 0 16px ${
                      storeStatus.isOpen
                        ? 'rgba(16, 185, 129, 0.7)'
                        : 'rgba(239, 68, 68, 0.7)'
                    }`,
                  },
                  '100%': {
                    boxShadow: `0 0 8px ${
                      storeStatus.isOpen
                        ? 'rgba(16, 185, 129, 0.4)'
                        : 'rgba(239, 68, 68, 0.4)'
                    }`,
                  },
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '13px',
                color: storeStatus.isOpen ? '#047857' : '#b91c1c',
                fontFamily: 'Jost',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              {storeStatus.isOpen ? 'OPEN NOW' : 'CLOSED'}
            </Typography>
          </Box>

          {/* Details Row with Enhanced Styling */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2.5,
              flexWrap: 'wrap',
              pl: { xs: 0, sm: 0.5 },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            {/* Store Hours Info */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: '12px',
                backgroundColor: 'rgba(107, 114, 128, 0.08)',
                border: '1px solid rgba(107, 114, 128, 0.15)',
              }}
            >
              <span
                role="img"
                aria-label="Clock"
                style={{
                  fontSize: '14px',
                  filter: 'grayscale(20%)',
                }}
              >
                🕒
              </span>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#4b5563',
                  fontFamily: 'Jost',
                  fontWeight: 500,
                }}
              >
                {storeStatus.isOpen
                  ? `Closes at ${displayClosingTime}`
                  : storeStatus.message}
              </Typography>
            </Box>

            {store.distance > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.15)',
                }}
              >
                <span
                  role="img"
                  aria-label="Location"
                  style={{
                    fontSize: '14px',
                    filter: 'grayscale(20%)',
                  }}
                >
                  📍
                </span>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#1e40af',
                    fontFamily: 'Jost',
                    fontWeight: 600,
                  }}
                >
                  {`${store.distance.toFixed(1)} mi away`}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>{' '}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 3,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {store.phone && typeof store.phone === 'string' && (
            <Typography
              sx={{
                color: '#6b7280',
                fontSize: '12px',
                fontFamily: 'Jost',
                fontWeight: 400,
              }}
            >
              {store.phone}
            </Typography>
          )}
          {store.pickup_enabled && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <GiPaperBagOpen size={20} color={environment.mainColor} />

              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#374151',
                  fontFamily: 'Jost',
                  fontWeight: 500,
                }}
              >
                Pickup
              </Typography>
            </Box>
          )}

          {store.delivery_enabled && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <FaTruck size={20} color={environment.mainColor} />
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#374151',
                  fontFamily: 'Jost',
                  fontWeight: 500,
                }}
              >
                Delivery
              </Typography>
            </Box>
          )}

          {!store.pickup_enabled && !store.delivery_enabled && (
            <Typography
              sx={{
                fontSize: '12px',
                color: '#374151',
                fontFamily: 'Jost', // Add this line
                fontWeight: 500,
              }}
            >
              <strong>Not Available for online ordering</strong>
            </Typography>
          )}
        </Box>
      </Box>

      {/* Services Section */}
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: '#fafafa',
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Button
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onStartOrder === 'function') {
              onStartOrder();
              return;
            }
            handleBranchSelect(index);
          }}
          sx={{
            backgroundColor: environment.mainColor,
            color: '#fff',
            fontWeight: 600,
            fontSize: '14px',
            fontFamily: 'Jost',
            textTransform: 'none',
            py: 1.25,
            px: 4,
            borderRadius: '12px',
            width: 'auto',
            minWidth: '140px',
            boxShadow: `0 4px 12px -2px ${environment.mainColor}40`,
            border: 'none',
            '&:hover': {
              backgroundColor: environment.mainColor,
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 20px -4px ${environment.mainColor}50`,
            },
            '&:active': {
              transform: 'translateY(0px)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {!store.delivery_enabled && !store.pickup_enabled
            ? 'View Menu'
            : 'Start Order'}
        </Button>
      </Box>
    </Card>
  );
};

export default BranchItem;
